import { useState, useEffect, useRef } from "react";

// ============================================
// ICD-10 CODE LOOKUP
// ============================================
// Pure reference tool — physicians search by description or code,
// results are displayed for reference and copy. Nothing is
// auto-populated or suggested. The physician drives the lookup.

interface ICD10Result {
  code: string;
  description: string;
}

function IconSearch({ className = "w-4 h-4" }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
  );
}

function IconCopy({ className = "w-3.5 h-3.5" }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
    </svg>
  );
}

function IconCheck({ className = "w-3.5 h-3.5" }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}

function IconBook({ className = "w-3.5 h-3.5" }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
    </svg>
  );
}

function IconChevronDown({ className = "w-3.5 h-3.5" }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
    </svg>
  );
}

function IconPlus({ className = "w-3 h-3" }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
    </svg>
  );
}

// ── Result row — tap to add, long-press/secondary to copy ────

function ResultRow({ result, onSelect, alreadyAdded }: { result: ICD10Result; onSelect?: (code: string, description: string) => void; alreadyAdded?: boolean }) {
  const [feedback, setFeedback] = useState<"" | "added" | "copied">("");

  const handleClick = () => {
    if (alreadyAdded) return;
    if (onSelect) {
      onSelect(result.code, result.description);
      setFeedback("added");
      setTimeout(() => setFeedback(""), 1500);
    } else {
      // Fallback: copy if no onSelect provided
      const text = `${result.code} - ${result.description}`;
      navigator.clipboard.writeText(text).then(() => {
        setFeedback("copied");
        setTimeout(() => setFeedback(""), 1500);
      });
    }
  };

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    const text = `${result.code} - ${result.description}`;
    navigator.clipboard.writeText(text).then(() => {
      setFeedback("copied");
      setTimeout(() => setFeedback(""), 1500);
    });
  };

  return (
    <button
      onClick={handleClick}
      className={`w-full flex items-start gap-3 px-3 py-2.5 text-left transition-colors ${
        alreadyAdded
          ? "bg-teal-50/50"
          : feedback
            ? "bg-teal-50"
            : "hover:bg-slate-50"
      }`}
    >
      {/* Code badge */}
      <span className={`flex-shrink-0 mt-0.5 px-2 py-0.5 rounded text-xs font-mono font-semibold tracking-wide ${
        alreadyAdded || feedback
          ? "bg-teal-100 text-teal-700"
          : "bg-slate-100 text-slate-700"
      }`}>
        {result.code}
      </span>
      {/* Description */}
      <span className={`flex-1 text-sm leading-snug min-w-0 ${alreadyAdded ? "text-slate-400" : "text-slate-600"}`}>
        {result.description}
      </span>
      {/* Action buttons */}
      <div className="flex-shrink-0 mt-0.5 flex items-center gap-1.5">
        {alreadyAdded && !feedback ? (
          <>
            <span className="flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium bg-teal-50 text-teal-500 border border-teal-100">
              <IconCheck className="w-3 h-3" />
              On case
            </span>
            <span
              role="button"
              onClick={handleCopy}
              className="flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium bg-slate-50 text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
            >
              <IconCopy className="w-3 h-3" />
              Copy
            </span>
          </>
        ) : feedback ? (
          <span className="flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium bg-teal-100 text-teal-600">
            <IconCheck className="w-3 h-3" />
            {feedback === "added" ? "Added" : "Copied"}
          </span>
        ) : (
          <>
            {onSelect && (
              <span className="flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium bg-teal-50 text-teal-600 border border-teal-200">
                <IconPlus className="w-3 h-3" />
                Add
              </span>
            )}
            <span
              role="button"
              onClick={handleCopy}
              className="flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium bg-slate-50 text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
            >
              <IconCopy className="w-3 h-3" />
              Copy
            </span>
          </>
        )}
      </div>
    </button>
  );
}

// ── Main component ───────────────────────────────────────────

export function ICD10CodeLookup({ defaultExpanded = false, onSelect, currentDiagnoses }: { defaultExpanded?: boolean; onSelect?: (code: string, description: string) => void; currentDiagnoses?: string }) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [searchTerm, setSearchTerm] = useState("");
  const [results, setResults] = useState<ICD10Result[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus input when panel expands
  useEffect(() => {
    if (isExpanded && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [isExpanded]);

  // Debounced NLM Clinical Tables API search
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (searchTerm.length < 2) {
      setResults([]);
      setHasSearched(false);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setIsLoading(true);
      setHasSearched(true);
      try {
        const response = await fetch(
          `https://clinicaltables.nlm.nih.gov/api/icd10cm/v3/search?sf=code,name&terms=${encodeURIComponent(
            searchTerm
          )}&maxList=15`
        );
        const data = await response.json();
        const formattedResults: ICD10Result[] = (data[3] || []).map(
          (item: string[]) => ({
            code: item[0],
            description: item[1],
          })
        );
        setResults(formattedResults);
      } catch (error) {
        console.error("[ICD10Lookup] Search error:", error);
        setResults([]);
      }
      setIsLoading(false);
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [searchTerm]);

  const handleClear = () => {
    setSearchTerm("");
    setResults([]);
    setHasSearched(false);
    inputRef.current?.focus();
  };

  return (
    <div className="overflow-hidden">
      {/* Header — always visible */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={`w-full flex items-center gap-3 px-5 py-[18px] text-left group transition-colors duration-150 hover:bg-slate-50 ${
          isExpanded ? "border-b border-slate-100" : ""
        }`}
      >
        <IconBook className="w-3.5 h-3.5 text-slate-400 opacity-90 flex-shrink-0" />
        <span className="text-sm font-medium text-slate-700 flex-1 min-w-0">
          ICD-10 Code Lookup
        </span>
        <span className="text-[10px] text-slate-400 font-medium mr-1">Reference</span>
        <IconChevronDown
          className={`w-3.5 h-3.5 text-slate-400 group-hover:text-slate-500 transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`}
        />
      </button>

      {/* Expandable content */}
      <div
        className={`transition-all duration-200 ease-in-out overflow-hidden ${
          isExpanded ? "max-h-[600px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="px-5 py-4 space-y-3">
          {/* Search input */}
          <div className="relative">
            <div className="absolute left-3 top-1/2 -translate-y-1/2">
              {isLoading ? (
                <div className="w-4 h-4 border-2 border-teal-400 border-t-transparent rounded-full animate-spin" />
              ) : (
                <IconSearch className="w-4 h-4 text-slate-400" />
              )}
            </div>
            <input
              ref={inputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by diagnosis or code…"
              className="w-full pl-10 pr-8 py-2.5 text-sm border border-slate-200 rounded-lg bg-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400 transition-colors"
            />
            {searchTerm && (
              <button
                onClick={handleClear}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>

          {/* Results */}
          {results.length > 0 && (
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <div className="max-h-[340px] overflow-y-auto divide-y divide-slate-100">
                {results.map((result) => (
                  <ResultRow key={result.code} result={result} onSelect={onSelect} alreadyAdded={!!currentDiagnoses?.includes(result.code)} />
                ))}
              </div>
              {/* Result count footer */}
              <div className="px-3 py-1.5 bg-slate-50 border-t border-slate-100">
                <p className="text-[10px] text-slate-400">
                  {results.length} result{results.length !== 1 ? "s" : ""} from NLM Clinical Tables
                </p>
              </div>
            </div>
          )}

          {/* Empty state — searched but no results */}
          {hasSearched && !isLoading && results.length === 0 && searchTerm.length >= 2 && (
            <div className="text-center py-6">
              <p className="text-sm text-slate-500">No codes found for "{searchTerm}"</p>
              <p className="text-[11px] text-slate-400 mt-1">Try a broader term or check spelling</p>
            </div>
          )}

          {/* Hint text */}
          {!hasSearched && (
            <p className="text-[10px] text-slate-400 leading-relaxed">
              Search by diagnosis name or ICD-10 code. {onSelect ? "Tap a result to add it to this case, or use Copy for clipboard." : "Results are from NLM Clinical Tables for reference only."}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}